import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDepartments } from '@/features/departments/api/use-departments';
import { useEmployee, useUpdateEmployee } from '@/features/employees/api/use-employees';
import { useEmployeeAccess } from '@/features/employees/hooks/use-employee-access';
import { usePlatformFacilities } from '@/features/facilities/api/use-facilities';
import { useSubsidiaries } from '@/features/settings/subsidiaries/api/use-subsidiaries';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/employees/$employeeId/edit')({
  component: EditEmployeePage,
});

function EditEmployeePage() {
  const { employeeId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { canManageEmployees, isLoading: accessLoading } = useEmployeeAccess();
  const { data: employee, isLoading } = useEmployee(employeeId);
  const updateMutation = useUpdateEmployee();

  const { data: deptData } = useDepartments({ page_size: '200' });
  const { data: facilityData } = usePlatformFacilities({ page_size: '200' });
  const { data: subsidiariesData } = useSubsidiaries({ includeNested: true });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [externalId, setExternalId] = useState('');
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (employee && !initialized) {
      setName(employee.name);
      setEmail(employee.email);
      setDepartmentId(employee.departmentId ?? '');
      setFacilityId(employee.facilityId ?? '');
      setExternalId(employee.employeeIdExternal ?? '');
      setSelectedOrgIds(employee.organizationAccess.map((a) => a.id));
      setInitialized(true);
    }
  }, [employee, initialized]);

  const canSubmit = name.trim().length > 0 && email.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      await updateMutation.mutateAsync({
        employeeId,
        data: {
          name: name.trim(),
          email: email.trim(),
          departmentId: departmentId || undefined,
          facilityId: facilityId || undefined,
          employeeIdExternal: externalId.trim() || undefined,
          organizationAccessIds: selectedOrgIds,
        },
      });
      toast.success(i18n.t('settings:toast.employeeUpdated'));
      void navigate({ to: '/employees/$employeeId', params: { employeeId } });
    } catch {
      toast.error(i18n.t('settings:toast.failedToUpdateEmployee'));
    }
  };

  if (accessLoading || isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!canManageEmployees) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-12 text-center">
          <h2 className="text-lg font-semibold">{t('accessDenied.title')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('accessDenied.employeeManageDenied')}
          </p>
          <Button
            variant="outline"
            onClick={() => void navigate({ to: '/dashboard' })}
            className="mt-4"
          >
            {t('accessDenied.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  if (!employee) {
    return <div className="p-6 text-muted-foreground">{t('employees.employeeNotFound')}</div>;
  }

  const departmentOptions = [
    { value: '', label: t('employees.noDepartment') },
    ...(deptData?.items.filter((d) => d.isActive).map((d) => ({ value: d.id, label: d.name })) ??
      []),
  ];
  const facilityOptions = [
    { value: '', label: t('employees.noFacility') },
    ...(facilityData?.items
      .filter((f) => f.status === 'active')
      .map((f) => ({ value: f.id, label: f.name })) ?? []),
  ];
  const subsidiaries = subsidiariesData?.subsidiaries ?? [];
  const hasSubsidiaries = subsidiaries.length > 0;

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            void navigate({ to: '/employees/$employeeId', params: { employeeId } })
          }
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> {t('employees.back')}
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">{t('employees.editEmployeeTitle')}</h1>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('employees.employeeDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('employees.name')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder={t('employees.fullName')}
                  value={name}
                  onChange={(e) => { setName(e.target.value); }}
                  required
                  data-testid="employee-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('employees.email')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('employees.emailPlaceholder')}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); }}
                  required
                  data-testid="employee-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="department">{t('employees.department')}</Label>
                <NativeSelect
                  id="department"
                  options={departmentOptions}
                  value={departmentId}
                  onChange={setDepartmentId}
                  data-testid="employee-department-select"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="facility">{t('employees.facility')}</Label>
                <NativeSelect
                  id="facility"
                  options={facilityOptions}
                  value={facilityId}
                  onChange={setFacilityId}
                  data-testid="employee-facility-select"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="externalId">{t('employees.externalId')}</Label>
              <Input
                id="externalId"
                placeholder={t('employees.hrSystemEmployeeId')}
                value={externalId}
                onChange={(e) => { setExternalId(e.target.value); }}
                data-testid="employee-external-id-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Company Access */}
        {hasSubsidiaries && (
          <Card>
            <CardHeader>
              <CardTitle>{t('employees.companyAccess')}</CardTitle>
              <CardDescription>
                {t('employees.companyAccessDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {subsidiaries.map((sub) => (
                  <label
                    key={sub.id}
                    className="flex items-center gap-3 rounded-md border p-3 hover:bg-accent/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOrgIds.includes(sub.id)}
                      onChange={(e) => {
                        setSelectedOrgIds((prev) =>
                          e.target.checked
                            ? [...prev, sub.id]
                            : prev.filter((id) => id !== sub.id)
                        );
                      }}
                      className="h-4 w-4 rounded border-input"
                      data-testid={`org-access-${sub.id}`}
                    />
                    <div>
                      <p className="text-sm font-medium">{sub.name}</p>
                      {sub.level > 1 && (
                        <p className="text-xs text-muted-foreground">
                          {'└'.padStart(sub.level, ' ')} Level {sub.level} subsidiary
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              void navigate({ to: '/employees/$employeeId', params: { employeeId } })
            }
          >
            {t('employees.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!canSubmit || updateMutation.isPending}
            data-testid="save-employee-button"
          >
            {updateMutation.isPending ? t('employees.saving') : t('employees.saveChanges')}
          </Button>
        </div>
      </form>
    </div>
  );
}
